import { Button, ButtonsGroup, Flex, Modal, Spacing, useModal } from '@billy/design-system'
import { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoicingSettingsSection } from '../../../../../../../../enums/invoicingSettingsSection'
import { useAutomaticPaymentRemindersModalForm } from '../../../../../../contexts/automaticPaymentRemindersModalFormContext'
import { AutomaticPaymentRemindersType } from '../../../../../../enums/automaticPaymentRemindersType'
import { useTrackAutomaticPaymentReminders } from '../../../../../../hooks/useTrackAutomaticPaymentReminders'
import { automaticPaymentRemindersModalTitleTKeyByType } from '../../constants/automaticPaymentRemindersModalTitleTKeyByType'
import { AutomaticPaymentRemindersDaysFields } from '../AutomaticPaymentRemindersDaysFields'
import { AutomaticPaymentRemindersEmailFields } from '../AutomaticPaymentRemindersEmailFields'

interface AutomaticPaymentRemindersModalContentProps {
  modalId: string
}

export const AutomaticPaymentRemindersModalContent = ({
  modalId,
}: AutomaticPaymentRemindersModalContentProps): ReactElement => {
  const { t } = useTranslation()
  const { submitForm, variant } = useAutomaticPaymentRemindersModalForm()
  const { close: closeModal } = useModal(modalId)
  const { trackAutomaticPaymentReminders } = useTrackAutomaticPaymentReminders({
    section: InvoicingSettingsSection.PaymentsAndReminders,
  })

  const hasDaysFields = variant !== AutomaticPaymentRemindersType.OnDueDate

  const handleCancelButtonClick = useCallback(() => {
    trackAutomaticPaymentReminders('modal - Cancel button clicked', variant)
    closeModal()
  }, [closeModal, trackAutomaticPaymentReminders, variant])

  const handleApplyButtonClick = useCallback(() => {
    trackAutomaticPaymentReminders('modal - Apply button clicked', variant)
    submitForm()
  }, [submitForm, trackAutomaticPaymentReminders, variant])

  return (
    <>
      <Modal.Header closable id={modalId} title={t(automaticPaymentRemindersModalTitleTKeyByType[variant])} />
      <Modal.Body>
        <Flex flexDirection="column" gap={Spacing.S16}>
          {hasDaysFields && <AutomaticPaymentRemindersDaysFields />}
          <AutomaticPaymentRemindersEmailFields />
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button onClick={handleCancelButtonClick} variant="text">
            {t('cancel')}
          </Button>
          <Button onClick={handleApplyButtonClick}>
            {t('settings.organization.external_invoice.automatic_payment_reminders.modal.apply')}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </>
  )
}
